import React, { useContext, useMemo, useState } from 'react';
import {
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Text } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import JobsContext from '../context/JobsContext';
import { Palette, radius, spacing, useThemePalette } from '../theme';
import { stripJobHtml } from '../utils';
import { track } from '../lib/analytics';
import { getPrimaryResume } from '../lib/match/fitScore';
import { buildApplyFields } from '../lib/apply/applyFields';
import ApplyKitChips from './ApplyKitChips';
import ApplyBrowser from './ApplyBrowser';
import Salary from './Salary';
import Tags from './Tags';

interface DescriptionProps {
  description: string;
  url: string;
  salary?: string;
  tags?: string[];
  date?: string;
  company?: string;
  position?: string;
  onClose: () => void;
}

const Description: React.FC<DescriptionProps> = ({
  description,
  salary,
  tags,
  date,
  company,
  position,
  url,
  onClose,
}) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  // Apply Kit: one-tap copy of the primary resume's contact fields, ready to
  // paste into the application form. Hidden when there's no resume.
  const { resumes } = useContext(JobsContext);
  const applyFields = useMemo(() => buildApplyFields(getPrimaryResume(resumes)), [resumes]);
  const [browserVisible, setBrowserVisible] = useState(false);

  const formattedDescription: string = description
    ? stripJobHtml(description)
    : t('description.openUrlMessage');

  // Apply: open the posting inside the app (in-app browser) on native so the
  // user can apply without leaving — opening does NOT mark the job as applied.
  // On web (already a browser) just navigate to the URL.
  const handleApply = () => {
    if (Platform.OS === 'web') {
      track('apply_open_external');
      Linking.openURL(url).catch((err) => console.error('Error opening URL:', err));
      return;
    }
    track('apply_browser_open');
    setBrowserVisible(true);
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title} numberOfLines={2}>
          {position}
        </Text>
        <TouchableOpacity onPress={onClose} style={styles.closeButton} hitSlop={8}>
          <MaterialCommunityIcons name="close" size={24} color={palette.textPrimary} />
        </TouchableOpacity>
      </View>

      {company ? <Text style={styles.company}>{company}</Text> : null}

      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator
      >
        <Text style={styles.description}>{formattedDescription}</Text>

        {salary ? <Salary salary={salary} /> : null}
        {date ? (
          <Text style={styles.date}>
            {t('description.postedOn')}: {date}
          </Text>
        ) : null}
        {tags && tags.length > 0 ? <Tags tags={tags} /> : null}
      </ScrollView>

      <ApplyKitChips fields={applyFields} />

      <TouchableOpacity style={styles.applyButton} onPress={handleApply} activeOpacity={0.85}>
        <Text style={styles.applyButtonText}>{t('description.applyViewDetails')}</Text>
      </TouchableOpacity>

      <ApplyBrowser
        visible={browserVisible}
        onClose={() => setBrowserVisible(false)}
        url={url}
        position={position}
        company={company}
        fields={applyFields}
      />
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    container: {
      flex: 1,
      padding: spacing.lg,
      backgroundColor: p.surface,
      borderRadius: radius.md,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: spacing.sm,
    },
    closeButton: {
      padding: spacing.xs,
    },
    title: {
      fontSize: 18,
      fontWeight: '700',
      flex: 1,
      marginRight: spacing.sm,
      color: p.textPrimary,
    },
    company: {
      fontSize: 15,
      color: p.textSecondary,
      marginBottom: spacing.md,
    },
    scrollContainer: { flex: 1 },
    scrollContent: { paddingBottom: spacing.sm },
    description: {
      fontSize: 15,
      lineHeight: 22,
      color: p.textPrimary,
      marginBottom: spacing.md,
    },
    date: {
      fontSize: 13,
      color: p.textMuted,
      marginVertical: spacing.sm,
    },
    applyButton: {
      backgroundColor: p.primary,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      borderRadius: radius.pill,
      alignItems: 'center',
      marginTop: spacing.md,
    },
    applyButtonText: {
      color: '#0F1115',
      fontWeight: '700',
      fontSize: 16,
    },
  });

export default Description;
